import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BlockStack,
  Button,
  Collapsible,
  Icon,
  InlineStack,
  Link,
  ResourceItem,
  ResourceList,
  Scrollable,
  Spinner,
  Text,
} from '@shopify/polaris';
import { PhoneInIcon, PhoneOutIcon, PlusIcon, PlayCircleIcon } from '@shopify/polaris-icons';
import CustomerNew from 'entities/customer/customer.new';
import helpers from 'helpers/index';
import { TypedPhoneLog, useMutateGetPhonelogs } from 'queries/phonelog.query';
import AudioPlayer from 'entities/phonelog/components/audio-player';

export function formatCreatedAt(timestamp: number | string): string {
  const date = new Date(Number(timestamp));
  const now = new Date();

  // reset giờ phút để so sánh chỉ theo ngày
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  const startOfDayBeforeYesterday = new Date(startOfToday);
  startOfDayBeforeYesterday.setDate(startOfDayBeforeYesterday.getDate() - 2);

  const startOfGivenDate = new Date(date);
  startOfGivenDate.setHours(0, 0, 0, 0);

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  if (startOfGivenDate.getTime() === startOfToday.getTime()) {
    return `${hours}:${minutes}`;
  } else if (startOfGivenDate.getTime() === startOfYesterday.getTime()) {
    return `Hôm qua`;
  } else if (startOfGivenDate.getTime() === startOfDayBeforeYesterday.getTime()) {
    return `Hôm kia`;
  } else {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${hours}:${minutes} ${day}/${month}`;
  }
}

export default function PhoneCallLog({ show }: { show: boolean }) {
  const { mutateAsync: getEntities, data } = useMutateGetPhonelogs();

  const [entities, setEntities] = useState<TypedPhoneLog[]>([]);

  const current_page = useRef(1);
  const [loading, setLoading] = useState(false);
  const [reachingEndData, setReachingEndData] = useState(false);

  // useEffect(() => {
  //   if (data) {
  //     setEntities(data.body);
  //     setTotalItems(data.totalItems);
  //   }
  // }, [data]);

  /**
   * Ngưỡng hiển thị ...
   */
  // const threshold_display_for_infinite_loading = useRef(20);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let { body } = await getEntities({
        sort: 'createdAt:desc',
        limit: 20,
        page: current_page.current || 1,
      });
      if (Array.isArray(body) && body.length < 1) {
        setReachingEndData(true);
      }
      let newData = [...body, ...entities];
      setEntities(newData);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!show) return;
    loadData();
  }, [show]);

  const [showNewCustomer, setShowNewCustomer] = useState(null);
  const currentNumber = useRef(null);
  const [currentPhonelogID, setCurrentPhonelogID] = useState(null);

  const doClick = useCallback(
    (phonglog_id: string, phonelog_number: any, user?: any) => {
      if (user) return;
      setCurrentPhonelogID(phonglog_id);
      setShowNewCustomer(true);
      currentNumber.current = phonelog_number;
    },
    [setCurrentPhonelogID]
  );

  const closeNewCustomerModal = useCallback(
    async (user_id: any) => {
      setShowNewCustomer(false);
      current_page.current = 1;
      if (currentPhonelogID)
        // await updateEntity({
        //   phonelog_id: currentPhonelogID,
        //   user_id: user_id,
        // });
        await helpers.sleep(1000);
      await loadData();
    },
    [currentPhonelogID]
  );

  const loadMoreAfterReachEnd = useCallback(() => {
    current_page.current = current_page.current + 1;
    loadData();
  }, [current_page]);

  const fetchDataMemo = useMemo(() => {
    if (reachingEndData) return;
    return helpers.debounce(() => {
      loadMoreAfterReachEnd?.call(this);
    }, 500);
  }, [loadMoreAfterReachEnd, reachingEndData]);

  const loadingMarkup = loading ? (
    <InlineStack align="center" blockAlign="center" gap="200">
      <br />
      <Spinner accessibilityLabel="Loading more content" size="small" />
      <br />
    </InlineStack>
  ) : null;

  const [collapsibleDiv, setCollapsibleDiv] = useState(null);

  const [idPlaying, setIdPlaying] = useState(false);
  const currentlyPlayingRef = useRef(null); // Lưu tham chiếu tới audio đang phát

  return (
    <div id="phone_call_log">
      {<CustomerNew onClose={closeNewCustomerModal} show={!!showNewCustomer} defaultNumber={currentNumber.current} />}

      <Scrollable onScrolledToBottom={fetchDataMemo} style={{ height: 'calc(100vh - 35px)' }} shadow focusable>
        <ResourceList
          resourceName={{ singular: 'customer', plural: 'customers' }}
          items={data?.body || []}
          renderItem={(item, index) => {
            const { id, destination, source, source_data, destination_data, disposition, recordingfile, duration, direction, createdAt, answeredBy } =
              item;
            // const media = <Avatar customer size="md" name={source_data?.display_name} source={source_data?.user_avatar} />;
            return (
              <ResourceItem
                verticalAlignment="center"
                id={id}
                url={destination_data ? '/users/view/' + destination_data.user_id : '#'}
                // media={media}
                onClick={() => {
                  setCollapsibleDiv(id);
                }}
              >
                <BlockStack gap="200">
                  <InlineStack gap="200" align="space-between" blockAlign="center">
                    <InlineStack gap="200" align="start" blockAlign="center">
                      <span>
                        <Icon source={direction === 'inbound' ? PhoneInIcon : PhoneOutIcon} tone={disposition === 'MISSED' ? 'critical' : 'info'} />
                      </span>
                      <div style={{ maxWidth: '200px' }}>
                        <BlockStack gap="100">
                          {source_data ? (
                            <Link removeUnderline monochrome onClick={() => window.location.replace(`customer/view/${source_data?.user_id}`)}>
                              <strong>{(source_data && source_data?.display_name) || source}</strong>
                            </Link>
                          ) : (
                            <span>{(source_data && source_data?.display_name) || source}</span>
                          )}
                          <Text as="span" variant="bodyXs">
                            {source_data ? source : ''}
                          </Text>
                        </BlockStack>
                      </div>
                    </InlineStack>
                    <Text as="p" tone="subdued" variant="bodySm">
                      {formatCreatedAt(createdAt)}
                    </Text>
                  </InlineStack>
                  <Collapsible
                    open={collapsibleDiv === id}
                    id="basic-collapsible"
                    transition={{ duration: '100ms', timingFunction: 'ease-in-out' }}
                    expandOnPrint
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" align="start" blockAlign="center">
                        <Text as="span" tone="subdued">
                          {helpers.secondToFullHourString(duration)}
                        </Text>
                        <Text as="span" tone="subdued">
                          Đến {destination}
                        </Text>
                        {answeredBy ? (
                          <Text as="span" tone="subdued">
                            Trả lời bởi {answeredBy}
                          </Text>
                        ) : null}
                      </InlineStack>
                      <InlineStack align="space-between" blockAlign="center">
                        {helpers.isLink(recordingfile) && duration > 0 ? (
                          <AudioPlayer
                            id={id}
                            duration={duration}
                            idPlaying={idPlaying}
                            setIdPlaying={setIdPlaying}
                            currentlyPlayingRef={currentlyPlayingRef}
                            audioUrl={recordingfile || ''}
                          />
                        ) : (
                          <Button disabled tone="critical" variant="plain" icon={PlayCircleIcon}>
                            00:00:00
                          </Button>
                        )}
                        <div>
                          {!source_data ? (
                            <Button icon={PlusIcon} variant="plain" onClick={() => doClick(id, source, source_data)}>
                              Thêm vào danh bạ
                            </Button>
                          ) : null}
                        </div>
                      </InlineStack>
                    </BlockStack>
                  </Collapsible>
                </BlockStack>
              </ResourceItem>
            );
          }}
        />
        {reachingEndData && (
          <Text as="p" alignment="center" tone="critical" variant="bodySm" fontWeight="bold">
            Không còn dữ liệu
          </Text>
        )}
        {loadingMarkup}
      </Scrollable>
    </div>
  );
}

